import React, { useState, useLayoutEffect, useEffect } from 'react';

// Keep this short on purpose — research on app onboarding consistently flags
// "information overload" (explaining every feature up front) as the top
// retention killer. This covers the 4 things a new user actually needs to
// find their way: the dashboard, where data comes from, the AI assistant,
// and where everything else lives. Everything else is discoverable later.
const STEPS = [
  {
    target: '[data-tour="dashboard-stats"]',
    title: 'Your business at a glance',
    description: 'Revenue, expenses, profit, and open invoices, all updated in real time as you use the app.',
  },
  {
    target: '[data-tour="nav-finances"]',
    title: 'Add expenses and income here',
    description: 'Scan a receipt, import a bank statement, or log things manually. This is where your data starts.',
  },
  {
    target: '[data-tour="nav-quantra"]',
    title: 'Ask Quantra anything',
    description: 'Your AI assistant reads your real numbers. Try asking "Am I profitable this month?"',
  },
  {
    target: '[data-tour="nav-tools"]',
    title: 'Everything else lives here',
    description: 'Invoicing, tax vault, subscriptions, and budgets, organized so the rest of the app stays out of your way until you need it.',
  },
];

export const OnboardingTour = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  // Lock scroll for the duration of the tour. The spotlight rect is measured
  // once per step and doesn't track scroll position, so letting the page
  // scroll underneath it makes the highlight drift away from its target —
  // simplest fix (and the standard approach for coachmark/spotlight UIs) is
  // to just not let that happen instead of recomputing on every scroll event.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  useLayoutEffect(() => {
    const target = document.querySelector(STEPS[step].target);
    if (target) {
      target.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      // Re-measure after the scroll settles
      const measure = () => setRect(target.getBoundingClientRect());
      measure();
      const t = setTimeout(measure, 350);
      return () => clearTimeout(t);
    }
    setRect(null);
  }, [step]);

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  const cardStyle = (() => {
    const cardWidth = 300;
    if (!rect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeBelow = spaceBelow > 220;
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - cardWidth - 16));
    return placeBelow
      ? { top: rect.bottom + 16, left }
      : { top: Math.max(16, rect.top - 210), left };
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, touchAction: 'none' }} onTouchMove={(e) => e.preventDefault()}>
      <div
        style={{
          position: 'fixed', inset: 0, touchAction: 'none',
          background: rect
            ? `radial-gradient(circle at ${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px, transparent ${Math.max(rect.width, rect.height) / 1.3}px, rgba(0,0,0,0.78) ${Math.max(rect.width, rect.height) / 1.3 + 50}px)`
            : 'rgba(0,0,0,0.78)',
          transition: 'background 0.3s ease',
        }}
        onClick={onSkip}
      />

      {rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.top - 6, left: rect.left - 6,
            width: rect.width + 12, height: rect.height + 12,
            border: '2px solid #a78bfa', borderRadius: 12,
            boxShadow: '0 0 0 4px rgba(167,139,250,0.2)',
            pointerEvents: 'none', transition: 'all 0.3s ease',
          }}
        />
      )}

      <div
        className="centris-card"
        style={{
          position: 'fixed', width: 300, maxWidth: 'calc(100vw - 32px)', padding: 20,
          borderRadius: 16, zIndex: 10000, transition: 'top 0.3s ease, left 0.3s ease',
          ...cardStyle,
        }}
      >
        <p style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Step {step + 1} of {STEPS.length}
        </p>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{current.title}</h3>
        <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5, marginBottom: 16 }}>{current.description}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onSkip}
            style={{ flex: 1, padding: '9px 0', borderRadius: 9, background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Skip
          </button>
          <button
            onClick={() => (isLast ? onComplete() : setStep((s) => s + 1))}
            style={{ flex: 1, padding: '9px 0', borderRadius: 9, background: '#7c3aed', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {isLast ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};
